import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import { SxProps, ThemeProvider } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import Bowser from 'bowser';
import orderBy from 'lodash/orderBy';
import React from 'react';
import { RouteContainer } from '../../components/route-container';
import { SelectAgentImpersonate } from '../../components/select-agent-impersonate';
import { AppContext } from '../../contexts/app-context';
import { AuthContext } from '../../contexts/auth-context';
import { darkTheme, uicolors } from '../../theme';
import {
  AuthorizeStatus,
  ContentCategoryName,
  ContentDocument,
  PromoTileData,
  PromoTileSectionData,
} from '../../types';
import { captureError } from '../../utils/capture-error';
import { isAgt } from '../../utils/is-agent';
import { ContestLeaderboards } from './contest-leaderboards';
import { AgentDashboard } from './dashboard';
import { Events } from './events';
import { NewsAndNotes } from './news-notes';
import { PromoCarousel } from './promo-carousel';
import { Today } from './today';
import { MobileYTShort } from './today/mobile-yt-short';

const detectMobile = (): boolean => {
  const parser = Bowser.getParser(window.navigator.userAgent);
  return parser.getPlatform().type === 'mobile';
};

export interface ChartRanking {
  AgtNo: string | null;
  AgtName: string | null;
  Total: number | null;
  RingFocusTitle?: string | null;
  RingFocusToolTip?: string | null;
}

export interface HomeDashData {
  IncomeTitle: string | null;
  IncomeTooltip: string | null;
  IncomeValue: number | null;
  UpcomingConvention: string | null;
  BaseshopTixTotal: number | null;
  HierarchyTixTotal: number | null;
  ImgFileLocation: string | null;
  LvlImagePath: string | null;
  NextLvlImagePath: string | null;
  BadgeGeneralInfo: string | null;
  BadgeCurrentLvlInfo: string | null;
  BadgeNextLvlInfo: string | null;
  EmbedURL?: string | null;
  IsPlaylist?: boolean;
  Recruits?: ChartRanking[];
  FasTracts?: ChartRanking[];
  GrossDeposits?: ChartRanking[];
  IPAP?: ChartRanking[];
  NewIPAgents?: ChartRanking[];
  RingFocus?: ChartRanking[];
  RingFocusTitle?: string | null;
  RingFocusToolTip?: string | null;
}

export const homeStyles: { [key: string]: SxProps } = {
  box: {
    letterSpacing: 1.1,
    textAlign: 'center',
    borderRadius: 2,
    border: '1px solid',
    borderColor: uicolors.black[600],
    color: uicolors.black[50],
    fontSize: { xs: 12, sm: 14 },
    fontFamily: 'Lato',
    textTransform: 'uppercase',
    background: `linear-gradient(135deg, #26292b 0%, #17191baa 100%)`,
  },
  button: {
    fontWeight: 700,
    letterSpacing: 1.1,
    whiteSpace: 'nowrap',
    fontSize: 10,
    fontFamily: 'Lato',
    background: `linear-gradient(135deg, #d3d6d9 0%, #9fa6ac 100%)`,
    borderRadius: 1,
  },
  section: {
    backgroundColor: uicolors.black[800],
    borderRadius: 2,
    px: { xs: 1, lg: 2 },
  },
  title: {
    textAlign: 'center',
    textTransform: 'uppercase',
    color: uicolors.black[50],
    fontSize: { xs: 16, sm: 24 },
    letterSpacing: 1.1,
    fontFamily: 'Lato',
  },
};

export function Home() {
  // Context
  const {
    state: { impersonatedAgent },
  } = React.useContext(AppContext);
  const {
    state: { user, session, status },
  } = React.useContext(AuthContext);
  // Hooks
  const isMobile = detectMobile();

  const staffAgtNo = session?.getAccessToken().decodePayload()['myStaffForAgt'];
  const AgtNo = impersonatedAgent?.agentno || staffAgtNo || user.username;

  // Query - NPN Status for impersonation
  const pathStatus = '/authorize/status';
  const queryStatus = useQuery({
    enabled: isAgt(AgtNo) && impersonatedAgent !== null,
    queryKey: [pathStatus, { AgtNo }],
    queryFn: async () => {
      const response: {
        data: AuthorizeStatus | null;
      } = await API.post('DashboardAPI', pathStatus, { body: { AgtNo } });

      return response.data;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Query - Dashboard Rankings & YouTube Short URL
  const dsPath = '/homepage/dashboard';
  const dsQueryKey = [dsPath, { AgtNo }];
  const dsQuery = useQuery({
    queryKey: dsQueryKey,
    queryFn: async () => {
      const response: {
        data: any[];
      } = await API.post('DashboardAPI', dsPath, { body: { AgtNo } });

      if (response.data) {
        const { data }: any = response;

        const categories = [
          'Recruits',
          'FasTracts',
          'IPAP',
          'GrossDeposits',
          'NewIPAgents',
          'RingFocus',
        ];
        let result: any = data;

        categories.forEach((category) => {
          result[category] = [];

          for (let i = 1; i <= 6; i++) {
            const idKey = `${category}TopID0${i}`;
            const nameKey = `${category}Name0${i}`;
            const totalKey = `${category}Total0${i}`;

            if (data[idKey]) {
              result[category].push({
                AgtNo: data[idKey],
                AgtName: data[nameKey],
                Total: data[totalKey],
              });
            }
          }
        });

        return result;
      }
      return [];
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Query - Homepage Content
  const path = '/homepage/dashboard/document';
  const query = useQuery({
    queryKey: [path],
    queryFn: async () => {
      const response: {
        data: ContentDocument[];
      } = await API.post('DashboardAPI', path, {
        body: {
          Categories: [
            ContentCategoryName['Leaderboards'],
            ContentCategoryName['News & Notes'],
            ContentCategoryName['Upcoming Events'],
          ],
        },
      });

      if (response.data) {
        return response.data;
      }

      return null;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Query - Promo Tiles
  const ptPath = '/promo-tiles';
  const ptQuery = useQuery({
    queryKey: [ptPath],
    queryFn: async () => {
      const response: {
        data: {
          Sections: PromoTileSectionData[];
          Promos: PromoTileData[];
        };
      } = await API.post('DashboardAPI', ptPath, {});
      if (response.data) {
        return response.data;
      }
      return { Sections: [], Promos: [] };
    },
  });

  const orderedPTSections = orderBy(
    ptQuery.data?.Sections,
    'OrderIndex',
    'asc',
  );

  // Check if New Agent should see Agent Race
  let newAgent = false;
  if (isAgt(AgtNo)) {
    // NPN Status for Current User from Context & Require Auth component check
    if (AgtNo === user.username && status && status.NPNVerified !== true) {
      newAgent = true;
    } else if (queryStatus.data && queryStatus.data.NPNVerified !== true) {
      newAgent = true;
    }
  }

  // Getting NPN Status for impersonation
  const statusLoading =
    queryStatus.isLoading && queryStatus.fetchStatus !== 'idle';

  return (
    <RouteContainer
      routeTitle="Home"
      headerContent={
        <ThemeProvider theme={darkTheme}>
          <SelectAgentImpersonate darkMode />
        </ThemeProvider>
      }
      darkMode
    >
      <Container maxWidth="lg" sx={{ color: uicolors.black[50] }}>
        <Stack p={0} mt={2} spacing={{ xs: 1, md: 2 }}>
          <Today
            AgtNo={AgtNo}
            data={dsQuery.data}
            isMobile={isMobile}
            loading={query.isLoading || dsQuery.isLoading || statusLoading}
            newAgent={newAgent}
          />

          {isMobile && dsQuery.data?.EmbedURL ? (
            <MobileYTShort
              videoSRC={dsQuery.data?.EmbedURL || ''}
              isPlaylist={dsQuery.data?.IsPlaylist || false}
            />
          ) : null}

          <AgentDashboard
            AgtNo={AgtNo}
            data={dsQuery.data}
            isMobile={isMobile}
          />
          <PromoCarousel
            section={orderedPTSections[0]}
            promos={ptQuery.data?.Promos.filter(
              (i) => i.Section === orderedPTSections[0].Section,
            )}
          />
          <Events
            content={query.data || []}
            loading={query.isFetching}
            isMobile={isMobile}
          />
          <PromoCarousel
            section={orderedPTSections[1]}
            promos={ptQuery.data?.Promos.filter(
              (i) => i.Section === orderedPTSections[1].Section,
            )}
          />
          <Stack
            width="100%"
            direction={{ xs: 'column-reverse', md: 'row' }}
            spacing={{ xs: 1, md: 2 }}
          >
            <Box flex={1}>
              <ContestLeaderboards
                content={query.data || []}
                loading={query.isFetching}
                isMobile={isMobile}
              />
            </Box>
            <Box flex={1}>
              <NewsAndNotes
                content={query.data || []}
                loading={query.isFetching}
                isMobile={isMobile}
              />
            </Box>
          </Stack>
          <PromoCarousel
            section={orderedPTSections[2]}
            promos={ptQuery.data?.Promos.filter(
              (i) => i.Section === orderedPTSections[2].Section,
            )}
          />
        </Stack>
      </Container>
    </RouteContainer>
  );
}
